import OlMap from 'ol/Map';
import Feature, { type FeatureLike } from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import { transform } from 'ol/proj';
import { createEmpty, extend, getHeight, getWidth, isEmpty, type Extent } from 'ol/extent';
import * as turf from '@turf/turf';

import { GeoJsonFeatureFactory } from '../geojson/GeoJsonFeatureFactory';
import { GeojsonFeatureCollection } from '@shared/geojson/types';

type RgbaColor = [number, number, number, number];

const DATA_PROJECTION = 'EPSG:4326';
const MAP_PROJECTION = 'EPSG:3857';

const FILL_ALPHA = 0.3;
const STROKE_COLOR = '#000000';
const STROKE_WIDTH = 1;
const DEFAULT_FILL_COLOR: RgbaColor = [136, 136, 136, FILL_ALPHA];

// Once a model occupies at least this many screen pixels its detailed outline is
// legible, so the convex-hull footprint hides to avoid clutter. Below it (zoomed
// out, where geojson-vt has dropped the detail) the hull keeps the model visible.
const HULL_MAX_FOOTPRINT_PX = 64;
const HULL_FILL_COLOR = 'rgba(120, 120, 120, 0.35)';
const HULL_STROKE_COLOR = 'rgba(80, 80, 80, 0.9)';
const HULL_STROKE_WIDTH = 1;

const EMPTY_STYLE = new Style();
const HULL_STYLE = new Style({
    fill: new Fill({ color: HULL_FILL_COLOR }),
    stroke: new Stroke({ color: HULL_STROKE_COLOR, width: HULL_STROKE_WIDTH }),
});

const parseColorString = (colorStr: string): RgbaColor => {
    if (!colorStr) return DEFAULT_FILL_COLOR;
    const parts = colorStr.split(',').map(s => Number(s.trim()));
    if (parts.length !== 3 || parts.some(isNaN)) return DEFAULT_FILL_COLOR;
    return [parts[0], parts[1], parts[2], FILL_ALPHA];
};

const hullStyleFn = (feature: FeatureLike, resolution: number): Style => {
    const maxDimension = feature.get('maxDimension');
    if (typeof maxDimension === 'number' && maxDimension / resolution >= HULL_MAX_FOOTPRINT_PX) {
        return EMPTY_STYLE;
    }
    return HULL_STYLE;
};

/**
 * Bounding box of a raw EPSG:4326 GeoJSON collection, transformed to the map
 * projection. Scans coordinates directly instead of building OpenLayers
 * geometries, so an over-detailed model does not allocate (or freeze) just to
 * resolve its extent.
 */
const computeExtent = (geojson: GeojsonFeatureCollection): Extent => {
    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;

    const visit = (node: unknown): void => {
        if (!Array.isArray(node)) return;
        const first = node[0];
        if (typeof first === 'number') {
            const lon = first;
            const lat = typeof node[1] === 'number' ? node[1] : first;
            if (lon < minLon) minLon = lon;
            if (lat < minLat) minLat = lat;
            if (lon > maxLon) maxLon = lon;
            if (lat > maxLat) maxLat = lat;
            return;
        }
        for (const child of node) visit(child);
    };

    for (const feature of geojson?.features ?? []) {
        visit(feature?.geometry?.coordinates);
    }

    if (minLon === Infinity) return createEmpty();

    const [minX, minY] = transform([minLon, minLat], DATA_PROJECTION, MAP_PROJECTION);
    const [maxX, maxY] = transform([maxLon, maxLat], DATA_PROJECTION, MAP_PROJECTION);
    return [minX, minY, maxX, maxY];
};

/**
 * Convex hull of a model's geometry as an OpenLayers polygon in the map
 * projection. The hull has only a handful of vertices, so rendering it on a
 * plain (non-tiled) layer is cheap and — unlike the tiled detail — never gets
 * dropped at low zoom.
 */
const computeHullFeature = (geojson: GeojsonFeatureCollection): Feature<Polygon> | null => {
    const hull = turf.convex(geojson as unknown as Parameters<typeof turf.convex>[0]);
    if (!hull) return null;

    const rings = hull.geometry.coordinates.map(ring =>
        ring.map(coord => transform([coord[0], coord[1]], DATA_PROJECTION, MAP_PROJECTION))
    );
    return new Feature(new Polygon(rings));
};

export interface DrawGeoJsonResult {
    extent: Extent;
}

/**
 * Renders IFC model outlines as client-side vector tiles (geojson-vt), mirroring
 * the DWG drawing pipeline. Each model gets its own tile index and
 * `VectorTileLayer`, so geometry is clipped and simplified per zoom level — this
 * is what prevents pathologically detailed polygons from freezing the main
 * thread, which neither the WebGL nor the single-image Canvas approach avoided.
 *
 * A gray convex-hull footprint per model is drawn underneath on a shared layer
 * so a model stays visible when zoomed out past where its tiled detail renders.
 */
export class ModelOutlineLayer {
    featureType: FeatureType = 'model_outline';
    map: OlMap = null;
    isAddedToMap = false;
    layerOpacity = 100;

    private zIndex = 10;

    private readonly layersByModelId = new Map<number, VectorTileLayer>();
    private readonly extentByModelId = new Map<number, Extent>();

    /**
     * Outlines only carry a fill color, so build one shared `Style` per distinct
     * color rather than one per feature per render.
     */
    private readonly styleCache = new Map<string, Style>();

    // One convex-hull polygon per model, shared on a single layer below the
    // tiles, shown only while the model is too small for its detail to read.
    private readonly hullsByModelId = new Map<number, Feature<Polygon>>();
    private readonly hullSource = new VectorSource<Feature<Polygon>>();
    private readonly hullLayer = new VectorLayer({
        source: this.hullSource,
        style: hullStyleFn,
    });

    addToMap(map: OlMap, zIndex = 10): this {
        if (this.isAddedToMap) return this;
        this.map = map;
        this.zIndex = zIndex;
        this.hullLayer.setZIndex(zIndex);
        this.hullLayer.setOpacity(this.opacityFraction());
        map.addLayer(this.hullLayer);
        this.isAddedToMap = true;
        return this;
    }

    removeFromMap(): void {
        if (!this.map) return;
        for (const id of Array.from(this.layersByModelId.keys())) {
            this.removeModelLayer(id);
        }
        this.map.removeLayer(this.hullLayer);
        this.hullSource.clear();
        this.hullsByModelId.clear();
        this.isAddedToMap = false;
    }

    drawGeoJSON(id: number, _name: string, geojson: GeojsonFeatureCollection): DrawGeoJsonResult {
        // Replace any layer previously drawn for this model.
        this.removeModelLayer(id);

        const extent = computeExtent(geojson);
        this.extentByModelId.set(id, extent);
        this.addHull(id, geojson, extent);

        if (!this.map) return { extent };

        const layer = new VectorTileLayer({
            source: GeoJsonFeatureFactory.createVectorTileSourceFromFeatureCollection(geojson),
            style: this.styleFunction,
        });
        layer.setZIndex(this.zIndex + 1);
        layer.setOpacity(this.opacityFraction());
        layer.set('id', this.featureType);
        layer.set('modelId', id);

        try {
            this.map.addLayer(layer);
            this.layersByModelId.set(id, layer);
        } catch (e) {
            console.warn('[ModelOutlineLayer] Failed to add model layer', e);
            layer.dispose();
        }

        return { extent };
    }

    removeFeaturesBySource(source: FeatureSource): void {
        if (source.modelId != null) {
            this.removeModelLayer(source.modelId);
        }
    }

    getExtentByModelId(id: number): Extent | null {
        return this.extentByModelId.get(id) ?? null;
    }

    /** Combined extent of every model currently drawn (empty if none). */
    getExtent(): Extent {
        const total = createEmpty();
        for (const extent of this.extentByModelId.values()) {
            extend(total, extent);
        }
        return total;
    }

    setLayerOpacity = (opacity: number): void => {
        this.layerOpacity = typeof opacity === 'number' ? opacity : 100;
        const fraction = this.opacityFraction();
        for (const layer of this.layersByModelId.values()) {
            layer.setOpacity(fraction);
        }
        this.hullLayer.setOpacity(fraction);
    };

    dispose(): void {
        this.removeFromMap();
        this.hullLayer.dispose();
        this.styleCache.clear();
    }

    private opacityFraction(): number {
        return utils.clamp(0.04, this.layerOpacity / 100, 1);
    }

    private addHull(id: number, geojson: GeojsonFeatureCollection, extent: Extent): void {
        if (isEmpty(extent)) return;
        const hull = computeHullFeature(geojson);
        if (!hull) return;

        hull.set('maxDimension', Math.max(getWidth(extent), getHeight(extent)));
        this.hullSource.addFeature(hull);
        this.hullsByModelId.set(id, hull);
    }

    private removeModelLayer(id: number): void {
        const layer = this.layersByModelId.get(id);
        if (layer) {
            if (this.map) this.map.removeLayer(layer);
            layer.dispose();
            this.layersByModelId.delete(id);
        }

        const hull = this.hullsByModelId.get(id);
        if (hull) {
            this.hullSource.removeFeature(hull);
            this.hullsByModelId.delete(id);
        }

        this.extentByModelId.delete(id);
    }

    private readonly styleFunction = (feature: FeatureLike): Style => {
        const colorValue = feature.get('color');
        const colorStr = typeof colorValue === 'string' ? colorValue : '';

        const cached = this.styleCache.get(colorStr);
        if (cached) return cached;

        const style = new Style({
            fill: new Fill({ color: parseColorString(colorStr) }),
            stroke: new Stroke({ color: STROKE_COLOR, width: STROKE_WIDTH }),
        });
        this.styleCache.set(colorStr, style);
        return style;
    };
}
